import Papa from 'papaparse';
import { SpeciesV2 } from '../../interfaces'; // Assuming this path is correct

// Import both CSV files
import SinpData from '../../assets/data/SINP_data_24062025.csv';
import AlienPlants from '../../assets/data/alien_plants_occitanie_biogeo_med.csv';

/**
 * Loads SINP species data and joins it with alien plant categories.
 *
 * This function performs the following steps:
 * 1. Parses the alien_plants_occitanie_biogeo_med.csv to create a lookup map
 * (cd_ref -> category).
 * 2. Parses the SINP_data_24062025.csv.
 * 3. During the SINP data processing, it uses the lookup map to add the
 * 'exotic' status to each SpeciesV2 object based on TaxaID_cdref.
 *
 * @returns A Promise that resolves with an array of SpeciesV2 objects,
 * enriched with the 'exotic' status.
 */
export const getSinpData = async (): Promise<SpeciesV2[]> => {
  // Step 1: Load and prepare the Alien Plants data
  const alienPlantsCategoryMap = await new Promise<Map<string, string>>((resolve, reject) => {
    Papa.parse(AlienPlants, {
      header: true,
      download: true, // Set to true if AlienPlants is a URL
      complete: (results) => {
        const tempMap = new Map<string, string>();
        if (results.data && Array.isArray(results.data)) {
          results.data.forEach((row: any) => {
            // Ensure 'cd_ref' and 'category' exist in your alien plants CSV
            const cdRef = String(row.cd_ref);
            const category = row.category;
            if (cdRef && category) {
              tempMap.set(cdRef, category);
            } else {
              console.warn(`Skipping alien plant row due to missing 'cd_ref' or 'category':`, row);
            }
          });
          resolve(tempMap);
        } else {
          reject(new Error('Failed to parse alien plants CSV data or no data found.'));
        }
      },
      error: (err) => {
        console.error('Error parsing alien plants CSV:', err);
        reject(err);
      },
    });
  });

  // Step 2: Load and process the SINP data, joining with alienPlantsCategoryMap
  return new Promise((resolve, reject) => {
    Papa.parse(SinpData, {
      header: true,
      download: true,
      complete: (results) => {
        if (results.data && Array.isArray(results.data)) {
          const transformedData: SpeciesV2[] = results.data
            .filter((row: any) => row.permid) // Ensure rows have a permid
            .map((row: any) => {
              // Combine date and heure for precise Date object
              let combinedDateTime = row.date_obs;
              if (row.heure_obs && row.heure_obs !== 'NA') {
                combinedDateTime = combinedDateTime + 'T' + row.heure_obs; // Corrected assignment
              }
              const observedDate = combinedDateTime ? new Date(combinedDateTime) : undefined;
              // --- JOINING PART ---
              const sinpTaxaIdCdref = String(row.TaxaID_cdref); // Get the ID from SINP data
              // Look up this ID in the pre-loaded alienPlantsCategoryMap
              const exoticStatus = alienPlantsCategoryMap.get(sinpTaxaIdCdref);
              // --- END JOINING PART ---
              const species: SpeciesV2 = {
                id: row.permid, // Assign permid string to id (UUID)
                // name: row.TaxaName || undefined,
                name: row.TaxaName || undefined,
                author: undefined, // Still not directly available in CSV
                family: row.family || undefined,
                commonNames: row.Common_Names || undefined,
                predictedName: undefined, // Not available in CSV
                images: [], // Not available in CSV, initialize as empty array
                taxaName: row.TaxaName || undefined,
                taxaId: row.TaxaID_cdnom || undefined,
                taxaRank: undefined, // Not available in CSV
                taxaRankLevel: undefined, // Not available in CSV
                group_label: row.lb_grtax || undefined,
                category: row.stabtio || undefined, // Mapped 'stabtio' to 'category'
                is_invasive: undefined, // Needs specific mapping logic if desired
                is_endemic: undefined, // Needs specific mapping logic if desired
                is_eu_directive: row.dhff || undefined, // Mapped 'dhff' (from CSV) to 'is_eu_directive'
                is_grin_uses: undefined, // Needs specific mapping logic if desired
                is_protected: row.pn || "No",
                iucn_red_list_category: row.lrm || undefined, // Global red list (from lrm)
                native: undefined, // Not directly available
                observed_data: observedDate,
                latitude: row.y_wgs84 ? parseFloat(row.y_wgs84) : undefined,
                longitude: row.x_wgs84 ? parseFloat(row.x_wgs84) : undefined,
                threatened: undefined, // Needs specific mapping
                alien_plants: exoticStatus, // Assigned from the lookup map
                european_red_list: row.lre || undefined,
                france_red_list: row.lr || undefined,
                znieff: row.znieff || undefined,
              };
              return species;
            });
          resolve(transformedData);
        } else {
          reject(new Error('Failed to parse SINP CSV data or no data found.'));
        }
      },
      error: (err) => reject(err),
    });
  });
};
